// AGC: Brand selector — search input + fuzzy match + add new brand
import React, { useState } from 'react'
import { Check, AlertCircle, Plus } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { VoiceFieldProps } from './types'

/**
 * AGC: Simple Levenshtein-based string similarity score (0-1)
 * AGC: Used for fuzzy brand deduplication when user types custom brand
 */
function getSimilarityScore(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().replace(/[\s\-_]/g, '')
  const s2 = str2.toLowerCase().replace(/[\s\-_]/g, '')
  if (s1 === s2) return 1
  // AGC: Check substring containment
  if (s1.includes(s2) || s2.includes(s1)) return 0.8
  const maxLen = Math.max(s1.length, s2.length)
  if (maxLen === 0) return 1
  // AGC: Levenshtein distance
  const m = s1.length, n = s2.length
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i]![0] = i
  for (let j = 0; j <= n; j++) dp[0]![j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1
      dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + cost)
    }
  }
  return 1 - dp[m]![n]! / maxLen
}

export const BrandField: React.FC<VoiceFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  lastResponse,
  wizardMode,
}) => {
  const { t } = useTranslation()
  const [brandSearchText, setBrandSearchText] = useState('')

  // AGC: Compute effective selected brand — from user input or auto-detected
  const effectiveBrand = inputValues.brand || (lastResponse?.parsedData?.brand as string) || ''

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', btn: 'px-4 py-3.5 text-base', checkIcon: 'w-5 h-5', input: 'px-4 py-3 text-base', grid: 'gap-2.5' }
    : { card: 'p-2', label: 'text-xs', btn: 'px-2 py-1.5 text-xs', checkIcon: 'w-3 h-3', input: 'px-2 py-1.5 text-sm', grid: 'gap-1' }

  return (
    <div className={`${sz.card} bg-white/5 border border-white/20 rounded-lg`}>
      <div className="mb-1.5">
        <span className={`text-white/80 ${sz.label} font-medium flex items-center gap-1`}>
          🏭 {info.fieldLabel}
        </span>
        {info.reason && (
          <p className="text-white/40 text-xs mt-0.5">{info.reason}</p>
        )}
      </div>

      {/* AGC: Search/filter input for brands */}
      <input
        type="text"
        placeholder={t('voice.searchBrand')}
        value={brandSearchText}
        onChange={(e) => setBrandSearchText(e.target.value)}
        className={`w-full ${sz.input} mb-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/30 focus:outline-none focus:border-emerald-500`}
      />

      {/* AGC: Brand buttons grid - scrollable, shows all brands for category */}
      <div className={`grid grid-cols-2 ${sz.grid} max-h-36 overflow-y-auto`}>
        {(info.suggestions || [])
          .filter((s: { value: string; label: string }) => {
            if (!brandSearchText) return true
            const search = brandSearchText.toLowerCase().replace(/[\s\-_]/g, '')
            const brand = s.value.toLowerCase().replace(/[\s\-_]/g, '')
            return brand.includes(search) || search.includes(brand) || getSimilarityScore(search, brand) > 0.5
          })
          .map((suggestion: { value: string; label: string; confidence?: number }) => (
            <button
              key={suggestion.value}
              onClick={() => {
                setInputValues(prev => ({ ...prev, brand: suggestion.value }))
                setBrandSearchText('')
              }}
              className={`flex items-center justify-between ${sz.btn} rounded text-left transition-colors ${
                effectiveBrand === suggestion.value
                  ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-300 font-medium'
                  : (suggestion.confidence || 0) > 90
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 hover:bg-emerald-500/20'
                    : 'bg-white/5 hover:bg-white/10 text-white/80 border border-transparent'
              }`}
            >
              <span className="truncate">{suggestion.label}</span>
              {effectiveBrand === suggestion.value && <Check className={`${sz.checkIcon} flex-shrink-0`} />}
            </button>
          ))}
      </div>

      {/* AGC: Show "Did you mean?" or "Add new brand" when search text is active */}
      {brandSearchText && (() => {
        const searchNorm = brandSearchText.toLowerCase().replace(/[\s\-_]/g, '')
        const bestMatch = (info.suggestions || []).reduce((best: { label: string; score: number }, s: { value: string; label: string }) => {
          const score = getSimilarityScore(searchNorm, s.value.toLowerCase().replace(/[\s\-_]/g, ''))
          return score > best.score ? { label: s.label, score } : best
        }, { label: '', score: 0 })

        // AGC: If very similar to existing brand, show "Did you mean?" instead of add
        if (bestMatch.score >= 0.7) {
          return (
            <div className="mt-1 p-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-300 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {t('voice.didYouMean')}: <strong>{bestMatch.label}</strong>?
            </div>
          )
        }

        // AGC: No close match - allow adding as new brand
        if (bestMatch.score < 0.5) {
          return (
            <button
              onClick={() => {
                const capitalizedBrand = brandSearchText.charAt(0).toUpperCase() + brandSearchText.slice(1)
                setInputValues(prev => ({ ...prev, brand: capitalizedBrand }))
                setBrandSearchText('')
              }}
              className="w-full flex items-center gap-2 mt-1 px-2 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded text-blue-300 text-xs transition-colors"
            >
              <Plus className="w-3 h-3" />
              {t('voice.addNewBrand')}: &quot;{brandSearchText}&quot;
            </button>
          )
        }

        return null
      })()}

      {/* AGC: Show currently selected brand */}
      {effectiveBrand && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-300 bg-emerald-500/10 rounded px-2 py-1">
          <Check className="w-3 h-3" />
          {t('voice.selectedBrand')}: <strong>{effectiveBrand}</strong>
        </div>
      )}
    </div>
  )
}
